/**
 * Maps an idle Button label to the in-flight gerund form used with `loading`.
 * Known first words conjugate (`Save` → `Saving...`, `Create Application` →
 * `Creating Application...`). Unknown labels keep their wording and gain `...`.
 */
const PROGRESSIVE_FIRST_WORDS: Record<string, string> = {
  activate: "Activating",
  add: "Adding",
  apply: "Applying",
  approve: "Approving",
  archive: "Archiving",
  assign: "Assigning",
  attach: "Attaching",
  book: "Booking",
  cancel: "Canceling",
  clear: "Clearing",
  close: "Closing",
  complete: "Completing",
  confirm: "Confirming",
  continue: "Continuing",
  copy: "Copying",
  create: "Creating",
  edit: "Editing",
  deactivate: "Deactivating",
  delete: "Deleting",
  disable: "Disabling",
  download: "Downloading",
  duplicate: "Duplicating",
  enable: "Enabling",
  enroll: "Enrolling",
  export: "Exporting",
  finalize: "Finalizing",
  generate: "Generating",
  grant: "Granting",
  import: "Importing",
  invite: "Inviting",
  lock: "Locking",
  mark: "Marking",
  merge: "Merging",
  move: "Moving",
  notify: "Notifying",
  open: "Opening",
  pay: "Paying",
  pin: "Pinning",
  preview: "Previewing",
  print: "Printing",
  process: "Processing",
  provision: "Provisioning",
  publish: "Publishing",
  refresh: "Refreshing",
  refund: "Refunding",
  register: "Registering",
  reject: "Rejecting",
  remove: "Removing",
  renew: "Renewing",
  resend: "Resending",
  reset: "Resetting",
  restore: "Restoring",
  retry: "Retrying",
  revoke: "Revoking",
  save: "Saving",
  search: "Searching",
  select: "Selecting",
  send: "Sending",
  show: "Showing",
  hide: "Hiding",
  sign: "Signing",
  start: "Starting",
  stop: "Stopping",
  submit: "Submitting",
  sync: "Syncing",
  transfer: "Transferring",
  unlock: "Unlocking",
  update: "Updating",
  upload: "Uploading",
  verify: "Verifying",
  view: "Viewing"
};

export const LOADING_ICON = "progress_activity" as const;
export const LOADING_ICON_CLASS_NAME = "animate-spin !block";

function matchCase(source: string, progressiveTitleCase: string): string {
  if (!source) return progressiveTitleCase;
  if (source === source.toUpperCase()) return progressiveTitleCase.toUpperCase();
  if (source[0] === source[0].toLowerCase()) {
    return progressiveTitleCase.charAt(0).toLowerCase() + progressiveTitleCase.slice(1);
  }
  return progressiveTitleCase;
}

export function toLoadingLabel(label: string): string {
  const trimmed = label.trim().replace(/\s+/g, " ");
  if (!trimmed) return trimmed;
  if (/(\.\.\.|…)$/.test(trimmed)) return trimmed;

  if (/\s+(&|and)\s+/i.test(trimmed)) {
    return `${trimmed}...`;
  }

  const space = trimmed.indexOf(" ");
  const first = space === -1 ? trimmed : trimmed.slice(0, space);
  const rest = space === -1 ? "" : trimmed.slice(space);

  if (first.toLowerCase().endsWith("ing")) {
    return `${trimmed}...`;
  }

  const progressive = PROGRESSIVE_FIRST_WORDS[first.toLowerCase()];
  if (!progressive) {
    return `${trimmed}...`;
  }

  return `${matchCase(first, progressive)}${rest}...`;
}
